/**
 * Lupa: janela de pesquisa com filtros, tabela paginada no servidor (25 por página) e seleção
 * por clique ou teclado (↑ ↓, PgUp/PgDn troca de página, Enter escolhe, Esc fecha). O foco
 * fica preso na janela enquanto ela está aberta.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Badge, Button, Select } from "@/components/ui";
import type { LookupSource } from "./types";

const PAGE = 25;

export function LookupDialog<T>({
  source,
  initialText,
  allowInactive,
  onClose,
  onPick,
}: {
  source: LookupSource<T>;
  initialText: string;
  allowInactive: boolean;
  onClose: () => void;
  onPick: (item: T) => void;
}) {
  const titleId = useId();
  const [text, setText] = useState(initialText);
  const [q, setQ] = useState(initialText.trim());
  const [page, setPage] = useState(0);
  const [active, setActive] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const boxRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  useEffect(() => {
    // só uma pesquisa NOVA volta à primeira página
    const next = text.trim();
    if (next === q) return;
    const id = setTimeout(() => {
      setQ(next);
      setPage(0);
      setActive(0);
    }, 250);
    return () => clearTimeout(id);
  }, [text, q]);

  const result = useQuery({
    queryKey: [...source.key, "lupa", q, page, showInactive, filters],
    queryFn: ({ signal }) =>
      source.search(
        { q, limit: PAGE, offset: page * PAGE, includeInactive: showInactive, filters },
        signal,
      ),
  });
  const items = result.data?.items ?? [];
  const total = result.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  function pick(n: number) {
    const item = items[n];
    if (item === undefined) return;
    if (source.toRow(item).inactive && !allowInactive) return;
    onPick(item);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setPage((p) => Math.min(p + 1, pages - 1));
      setActive(0);
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setPage((p) => Math.max(p - 1, 0));
      setActive(0);
    } else if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "BUTTON") {
      e.preventDefault();
      e.stopPropagation();
      pick(active);
    } else if (e.key === "Tab") {
      // foco preso na janela
      const focusable = boxRef.current?.querySelectorAll<HTMLElement>(
        "input, select, button:not([disabled]), [tabindex='0']",
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:pt-20">
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-4xl rounded-lg border bg-card p-4 shadow-lg"
        onKeyDown={onKeyDown}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 id={titleId} className="font-semibold">
            Pesquisar {source.noun}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar (Esc)
          </Button>
        </div>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="block min-w-60 flex-1 text-sm">
            <span className="font-medium">Pesquisa</span>
            <input
              ref={searchRef}
              className="mt-1 h-10 w-full rounded-md border bg-card px-3 text-sm outline-none focus:border-ring"
              value={text}
              placeholder={source.placeholder}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          {(source.filters ?? []).map((f) => (
            <label key={f.name} className="block w-40 text-sm">
              <span className="font-medium">{f.label}</span>
              <Select
                className="mt-1"
                value={filters[f.name] ?? ""}
                onChange={(e) => {
                  setFilters((x) => ({ ...x, [f.name]: e.target.value }));
                  setPage(0);
                  setActive(0);
                }}
              >
                <option value="">Todos</option>
                {f.options.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </label>
          ))}
          <label className="flex h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setPage(0);
                setActive(0);
              }}
            />
            Mostrar inativos
          </label>
        </div>
        <div className="max-h-[55vh] overflow-auto rounded-md border">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-card text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {source.columns.map((c) => (
                  <th key={c.header} className="px-3 py-2 font-medium">
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item, n) => {
                const row = source.toRow(item);
                const blocked = row.inactive && !allowInactive;
                return (
                  <tr
                    key={row.id}
                    aria-selected={n === active}
                    aria-disabled={blocked || undefined}
                    className={`cursor-pointer ${n === active ? "bg-muted" : ""} ${
                      blocked ? "opacity-60" : ""
                    }`}
                    onMouseEnter={() => setActive(n)}
                    onClick={() => pick(n)}
                  >
                    {source.columns.map((c) => (
                      <td key={c.header} className={`px-3 py-2 ${c.className ?? ""}`}>
                        {c.cell(item)}
                        {c.header === "Situação" && row.inactive && (
                          <span className="ml-1">
                            <Badge tone="warn">Inativo</Badge>
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {result.data && items.length === 0 && (
                <tr>
                  <td
                    colSpan={source.columns.length}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    {source.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            {result.isFetching
              ? "Pesquisando…"
              : `${total} encontrado(s) · página ${page + 1} de ${pages} · ↑ ↓ escolhe, Enter seleciona, PgUp/PgDn muda a página`}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => {
                setPage((p) => p - 1);
                setActive(0);
              }}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= pages - 1}
              onClick={() => {
                setPage((p) => p + 1);
                setActive(0);
              }}
            >
              Próxima
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
